import { useState } from "react";
import { Card, Table, Tag, Select, Input, Button } from "antd";
import { Search, RefreshCw } from "lucide-react";
import { useAuditLogs } from "../../../api-hooks/auditLogs";
import { useAdminList } from "../../../api-hooks/admins";
import dayjs from "dayjs";

const ACTION_COLORS: Record<string, string> = {
  create: "green",
  update: "blue",
  delete: "red",
  activate: "green",
  suspend: "orange",
  change_plan: "purple",
  extend_trial: "cyan",
  login: "geekblue",
};

const ENTITY_COLORS: Record<string, string> = {
  company: "blue",
  admin: "purple",
  plan: "green",
  industry: "orange",
};

export default function ActivityLog() {
  const [page, setPage] = useState(1);
  const [adminFilter, setAdminFilter] = useState<number | undefined>();
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | undefined>();

  const { data, isLoading, refetch } = useAuditLogs({
    admin_id: adminFilter,
    entity_type: entityTypeFilter,
    page,
    limit: 50,
  });

  const { data: admins = [] } = useAdminList();

  const logs = data?.data ?? [];
  const total = data?.total ?? 0;

  const columns = [
    {
      title: "Time",
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (d: string) => (
        <div className="text-xs text-slate-500">{dayjs(d).format("DD MMM YY HH:mm")}</div>
      ),
    },
    {
      title: "Admin",
      dataIndex: "admin_name",
      key: "admin_name",
      width: 140,
      render: (n: string) => <span className="font-medium text-slate-700">{n}</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 130,
      render: (a: string) => (
        <Tag color={ACTION_COLORS[a] || "default"} className="capitalize">
          {a.replace(/_/g, " ")}
        </Tag>
      ),
    },
    {
      title: "Entity",
      key: "entity",
      width: 160,
      render: (_: any, row: any) => (
        <div>
          <Tag color={ENTITY_COLORS[row.entity_type] || "default"} className="text-xs capitalize">
            {row.entity_type}
          </Tag>
          {row.entity_name && (
            <span className="text-xs text-slate-500 ml-1">{row.entity_name}</span>
          )}
        </div>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (d: string | null) => (
        <span className="text-sm text-slate-600">{d ?? "—"}</span>
      ),
    },
    {
      title: "IP",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 120,
      render: (ip: string | null) => (
        <span className="text-xs text-slate-400 font-mono">{ip ?? "—"}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Activity Log</h2>
          <p className="text-slate-500 text-sm mt-0.5">{total} events recorded</p>
        </div>
        <Button icon={<RefreshCw size={14} />} onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      <Card className="mb-4 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex gap-3 flex-wrap">
          <Select
            placeholder="Filter by admin"
            allowClear
            style={{ width: 200 }}
            onChange={(v) => { setAdminFilter(v); setPage(1); }}
            options={admins.map((a) => ({ value: a.id, label: a.name }))}
          />
          <Select
            placeholder="Filter by entity"
            allowClear
            style={{ width: 160 }}
            onChange={(v) => { setEntityTypeFilter(v); setPage(1); }}
            options={[
              { value: "company", label: "Company" },
              { value: "admin", label: "Admin" },
              { value: "plan", label: "Plan" },
              { value: "industry", label: "Industry" },
            ]}
          />
        </div>
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={logs}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: 50,
            total,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `${t} events`,
          }}
          size="small"
        />
      </Card>
    </div>
  );
}
